import { join } from 'path';
import {
  resolvePath,
  exists,
  isDirectory,
  readDir,
  readFile,
} from '@/utils/fs';
import {
  validateJsonReport,
  validateHtmlReportDir,
  validateTraceDir,
} from './validators';
import { CLIOptions, FileSystemError } from '@/types';

/**
 * Result of the discovery process.
 */
export interface DiscoveryResult {
  jsonReport: string;
  htmlReport?: string | undefined;
  traceDir?: string | undefined;
}

/**
 * Service to discover Playwright test report files with smart scanning.
 */
export class ReportDiscoveryService {
  constructor(private readonly reportDir: string) {}

  /**
   * Discover report files based on CLI options with smart scanning.
   */
  async discover(options: CLIOptions): Promise<DiscoveryResult> {
    const baseDir = resolvePath(options.reportDirectory, 'Report directory');

    // JSON report discovery with manual override
    const jsonReportPath =
      options.jsonReport ?? (await this.findJsonReport(baseDir));
    await validateJsonReport(jsonReportPath);

    // HTML report discovery (only if upload requested)
    let htmlReportDir: string | undefined;
    if (options.uploadHtml) {
      htmlReportDir =
        options.htmlReport ?? (await this.findHtmlReport(baseDir));
      if (htmlReportDir) {
        await validateHtmlReportDir(htmlReportDir);
      }
    }

    // Trace files discovery (as fallback when not found in JSON attachments)
    let traceDirectory: string | undefined;
    if (options.uploadTraces) {
      // Check if traces are available in JSON attachments first
      const hasTracesInJson =
        await this.checkTracesInJsonReport(jsonReportPath);

      if (hasTracesInJson) {
        // Traces found in JSON attachments, no need for separate trace directory
        traceDirectory = undefined;
      } else {
        // No traces in JSON, try to find trace directory as fallback
        if (options.traceDir) {
          // User explicitly specified trace directory - validate it exists
          await validateTraceDir(options.traceDir);
          traceDirectory = options.traceDir;
        } else {
          // Auto-discover traces - gracefully handle missing traces (normal when tests pass)
          traceDirectory = await this.findTraceDir(baseDir);
          if (traceDirectory) {
            await validateTraceDir(traceDirectory);
          }
          // If traceDirectory is undefined, that's OK - no traces found (tests likely passed)
        }
      }
    }

    return {
      jsonReport: jsonReportPath,
      htmlReport: htmlReportDir,
      traceDir: traceDirectory,
    };
  }

  /**
   * Smart JSON report discovery - scan for valid Playwright JSON files
   */
  private async findJsonReport(baseDir: string): Promise<string> {
    // Try common patterns first (faster)
    const commonPatterns = [
      join(baseDir, 'report.json'), // Standard Playwright
      join(baseDir, 'results.json'), // Alternative name
      join(baseDir, 'test-results.json'), // Another common name
      join(baseDir, 'playwright-report', 'report.json'), // Nested structure
      join(baseDir, 'playwright-report', 'results.json'), // Nested alternative
    ];

    for (const candidate of commonPatterns) {
      if (await exists(candidate)) {
        // Validate it's actually a Playwright report
        if (await this.isValidPlaywrightJson(candidate)) {
          return candidate;
        }
      }
    }

    // If no common patterns found, scan all JSON files
    const jsonFiles = await this.scanForJsonFiles(baseDir);

    for (const jsonFile of jsonFiles) {
      if (await this.isValidPlaywrightJson(jsonFile)) {
        return jsonFile;
      }
    }

    throw new FileSystemError(
      `No valid Playwright JSON report found in ${baseDir}.\n` +
        '💡 Looked for: report.json, results.json, test-results.json\n' +
        '💡 Use --json-report <path> to specify exact location'
    );
  }

  /**
   * Smart HTML report discovery - find directory containing index.html
   */
  private async findHtmlReport(baseDir: string): Promise<string> {
    // Try common patterns first
    const commonPatterns = [
      baseDir, // Current directory
      join(baseDir, 'html-report'), // Standard name
      join(baseDir, 'playwright-report'), // Standard Playwright
      join(baseDir, 'report'), // Alternative
      join(baseDir, 'test-report'), // Alternative
    ];

    for (const candidate of commonPatterns) {
      if (await isDirectory(candidate)) {
        const indexPath = join(candidate, 'index.html');
        if (await exists(indexPath)) {
          return candidate;
        }
      }
    }

    // Scan for any directory containing index.html
    const htmlDirs = await this.scanForHtmlDirectories(baseDir);
    if (htmlDirs.length > 0) {
      const candidateDir = htmlDirs[0];
      if (candidateDir !== undefined) {
        return candidateDir;
      }
    }

    throw new FileSystemError(
      `No HTML report directory with index.html found in ${baseDir}.\n` +
        '💡 Use --html-report <path> to specify exact location'
    );
  }

  /**
   * Smart trace directory discovery
   */
  private async findTraceDir(baseDir: string): Promise<string | undefined> {
    // Try common patterns
    const commonPatterns = [
      join(baseDir, 'trace'),
      join(baseDir, 'traces'),
      join(baseDir, 'test-results'),
      join(baseDir, 'playwright-report', 'trace'),
      join(baseDir, 'playwright-report', 'traces'),
    ];

    for (const candidate of commonPatterns) {
      if (await isDirectory(candidate)) {
        // Check if directory contains any trace files
        if (await this.containsTraceFiles(candidate)) {
          return candidate;
        }
      }
    }

    // Scan for directories containing .zip files (common trace format)
    const traceDirs = await this.scanForTraceDirectories(baseDir);
    if (traceDirs.length > 0) {
      const candidateTraceDir = traceDirs[0];
      if (candidateTraceDir !== undefined) {
        return candidateTraceDir;
      }
    }

    // Return undefined if no traces found (normal when tests pass)
    return undefined;
  }

  /**
   * Check if JSON report contains trace files in attachments
   */
  private async checkTracesInJsonReport(jsonPath: string): Promise<boolean> {
    try {
      const content = await readFile(jsonPath);
      const data = JSON.parse(content);

      if (!data?.suites || !Array.isArray(data.suites)) {
        return false;
      }

      // Recursively check for trace attachments in the report
      return this.hasTraceAttachments(data.suites);
    } catch {
      return false;
    }
  }

  /**
   * Recursively check if suites contain trace attachments
   */
  private hasTraceAttachments(suites: unknown[]): boolean {
    for (const suite of suites) {
      const suiteRecord = suite as Record<string, unknown>;

      // Check specs in this suite
      if (Array.isArray(suiteRecord.specs)) {
        for (const spec of suiteRecord.specs as unknown[]) {
          if (this.hasTraceAttachmentsInSpec(spec)) {
            return true;
          }
        }
      }

      // Check nested suites
      if (Array.isArray(suiteRecord.suites)) {
        if (this.hasTraceAttachments(suiteRecord.suites as unknown[])) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if a spec contains trace attachments
   */
  private hasTraceAttachmentsInSpec(spec: unknown): boolean {
    const specRecord = spec as Record<string, unknown>;
    if (!Array.isArray(specRecord.tests)) return false;

    for (const test of specRecord.tests as unknown[]) {
      const testRecord = test as Record<string, unknown>;
      if (!Array.isArray(testRecord.results)) continue;

      for (const result of testRecord.results as unknown[]) {
        const resultRecord = result as Record<string, unknown>;
        if (Array.isArray(resultRecord.attachments)) {
          for (const attachment of resultRecord.attachments as unknown[]) {
            const attachmentRecord = attachment as Record<string, unknown>;

            // Check if this attachment is a trace file
            if (this.isTraceAttachment(attachmentRecord)) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Check if an attachment is a trace file
   */
  private isTraceAttachment(attachment: Record<string, unknown>): boolean {
    const name = attachment.name as string;
    const contentType = attachment.contentType as string;

    if (!name || !contentType) return false;

    const lowerName = name.toLowerCase();
    const lowerContentType = contentType.toLowerCase();

    // Check content type for trace files
    const traceContentTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'application/octet-stream',
    ];

    if (traceContentTypes.includes(lowerContentType)) {
      // Additional check for trace files by name
      if (
        lowerName.includes('trace') ||
        lowerName.endsWith('.trace') ||
        lowerName.endsWith('.zip')
      ) {
        return true;
      }
    }

    // Fallback check by name
    return lowerName.includes('trace') || lowerName.endsWith('.trace');
  }

  /**
   * Validate if a JSON file is a valid Playwright report
   */
  private async isValidPlaywrightJson(filePath: string): Promise<boolean> {
    try {
      const content = await readFile(filePath);
      const data = JSON.parse(content);

      // Check for required Playwright report structure
      return (
        data &&
        typeof data === 'object' &&
        'config' in data &&
        'suites' in data &&
        'stats' in data &&
        Array.isArray(data.suites)
      );
    } catch {
      return false;
    }
  }

  /**
   * Scan directory recursively for JSON files
   */
  private async scanForJsonFiles(
    dir: string,
    maxDepth: number = 2
  ): Promise<string[]> {
    const jsonFiles: string[] = [];

    try {
      await this.scanDirectory(
        dir,
        jsonFiles,
        file => file.endsWith('.json'),
        0,
        maxDepth
      );
    } catch {
      // Ignore scan errors
    }

    return jsonFiles;
  }

  /**
   * Scan for directories containing index.html
   */
  private async scanForHtmlDirectories(
    dir: string,
    maxDepth: number = 2
  ): Promise<string[]> {
    const htmlDirs: string[] = [];

    try {
      await this.scanDirectoryForHtml(dir, htmlDirs, 0, maxDepth);
    } catch {
      // Ignore scan errors
    }

    return htmlDirs;
  }

  /**
   * Scan for directories containing trace files
   */
  private async scanForTraceDirectories(
    dir: string,
    maxDepth: number = 2
  ): Promise<string[]> {
    const traceDirs: string[] = [];

    try {
      await this.scanDirectoryForTraces(dir, traceDirs, 0, maxDepth);
    } catch {
      // Ignore scan errors
    }

    return traceDirs;
  }

  /**
   * Generic directory scanner for files
   */
  private async scanDirectory(
    dir: string,
    results: string[],
    filter: (file: string) => boolean,
    currentDepth: number,
    maxDepth: number
  ): Promise<void> {
    if (currentDepth > maxDepth) return;

    const entries = await readDir(dir);

    for (const entry of entries) {
      if (await isDirectory(entry)) {
        await this.scanDirectory(
          entry,
          results,
          filter,
          currentDepth + 1,
          maxDepth
        );
      } else if (filter(entry)) {
        results.push(entry);
      }
    }
  }

  /**
   * Scan for HTML directories
   */
  private async scanDirectoryForHtml(
    dir: string,
    results: string[],
    currentDepth: number,
    maxDepth: number
  ): Promise<void> {
    if (currentDepth > maxDepth) return;

    // Check if current directory has index.html
    const indexPath = join(dir, 'index.html');
    if (await exists(indexPath)) {
      results.push(dir);
      return; // Don't scan subdirectories if we found one
    }

    // Scan subdirectories
    const entries = await readDir(dir);
    for (const entry of entries) {
      if (await isDirectory(entry)) {
        await this.scanDirectoryForHtml(
          entry,
          results,
          currentDepth + 1,
          maxDepth
        );
      }
    }
  }

  /**
   * Scan for trace directories
   */
  private async scanDirectoryForTraces(
    dir: string,
    results: string[],
    currentDepth: number,
    maxDepth: number
  ): Promise<void> {
    if (currentDepth > maxDepth) return;

    // Check if current directory contains trace files
    if (await this.containsTraceFiles(dir)) {
      results.push(dir);
      return;
    }

    // Scan subdirectories
    const entries = await readDir(dir);
    for (const entry of entries) {
      if (await isDirectory(entry)) {
        await this.scanDirectoryForTraces(
          entry,
          results,
          currentDepth + 1,
          maxDepth
        );
      }
    }
  }

  /**
   * Check if directory contains trace files
   */
  private async containsTraceFiles(dir: string): Promise<boolean> {
    try {
      const entries = await readDir(dir);
      return entries.some(
        entry =>
          entry.endsWith('.zip') ||
          entry.endsWith('.trace') ||
          entry.includes('trace')
      );
    } catch {
      return false;
    }
  }
}
